import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	type ColumnFiltersState,
	type ColumnOrderState,
	type ColumnSizingState,
	type ExpandedState,
	type GroupingState,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	type OnChangeFn,
	type Row,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import {
	Copy,
	Filter,
	GripVertical,
	Group,
	ListChevronsDownUp,
	type LucideIcon,
	Merge,
	Plus,
	RotateCcw,
	Send,
	Settings2,
	Trash2,
	X,
} from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useMattersCache, useSettingsCache } from "@/shared/cache";
import { useDateParams } from "@/shared/hooks/useDateParams";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useScopedRowSelectionState } from "@/shared/hooks/useScopedRowSelectionState";
import {
	DatePicker,
	DatePickerDefaultFooter,
	type DateRange,
} from "@/v2/components/date-picker/DatePicker";
import { MatterLockButton } from "@/v2/components/MatterLockButton";
import { Button } from "@/v2/components/ui/button";
import { ButtonGroup } from "@/v2/components/ui/button-group";
import { Checkbox } from "@/v2/components/ui/checkbox";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { isCompleteDateRange } from "@/v2/lib/dates";
import { cn } from "@/v2/lib/utils";
import { useEntryActions } from "./Entries/actions/useEntryActions";
import {
	COLUMN_LABELS,
	DEFAULT_COLUMN_ORDER,
	DEFAULT_COLUMN_VISIBILITY,
	type EntriesViewMeta,
	getEntriesColumns,
} from "./Entries/columns";
import { EntriesTable } from "./Entries/EntriesTable";
import { EntriesTimer } from "./Entries/EntriesTimer";
import {
	computeDateFilter,
	type DateFilter,
	makeEntryPredicate,
} from "./Entries/entryFilter";
import { isDefaultFilters } from "./Entries/filters/defaults";
import { FilterMenu } from "./Entries/filters/FilterMenu";
import type { EntryFilterState } from "./Entries/filters/types";
import { useFilterOptions } from "./Entries/filters/useFilterOptions";
import { useFilters } from "./Entries/filters/useFilters";
import type { Entry } from "./Entries/normalize";
import { StatsBar } from "./Entries/StatsBar";
import { StatusIcon } from "./Entries/StatusIcon";
import { SearchBar } from "./Entries/search/SearchBar";
import { useFuseSearch } from "./Entries/search/useFuseSearch";
import { useConfiguredTimekeepers } from "./Entries/timekeepers";
import { useAllEntries } from "./Entries/useAllEntries";
import {
	columnOrderParser,
	DEFAULT_SORTING,
	sortingParser,
	visibilityParser,
} from "./Entries/viewParams";

function IconButton({
	icon: Icon,
	iconNode,
	label,
	onClick,
	disabled,
}: {
	icon?: LucideIcon;
	iconNode?: ReactNode;
	label: string;
	onClick?: () => void;
	disabled?: boolean;
}) {
	return (
		<TooltipButton
			variant="outline"
			size="icon"
			aria-label={label}
			onClick={onClick}
			disabled={disabled}
			className="group"
			tooltip={label}
		>
			{iconNode ?? (Icon ? <Icon /> : null)}
		</TooltipButton>
	);
}

function getSelectionScopeKey(
	dateFilter: DateFilter,
	filters: EntryFilterState,
	searchQuery: string,
): string {
	return [
		dateFilter.from ?? "",
		dateFilter.to ?? "",
		searchQuery.trim(),
		JSON.stringify(filters),
	].join("|");
}

function ColumnsMenu({
	columnVisibility,
	onColumnVisibilityChange,
	columnOrder,
	onColumnOrderChange,
	showTimekeeperColumn,
}: {
	columnVisibility: VisibilityState;
	onColumnVisibilityChange: (next: VisibilityState) => void;
	columnOrder: ColumnOrderState;
	onColumnOrderChange: (next: ColumnOrderState) => void;
	showTimekeeperColumn: boolean;
}) {
	const columnLabels = useMemo(() => {
		if (showTimekeeperColumn) return COLUMN_LABELS;
		return Object.fromEntries(
			Object.entries(COLUMN_LABELS).filter(([key]) => key !== "timekeeper"),
		);
	}, [showTimekeeperColumn]);
	const defaultColumnOrder = useMemo(
		() =>
			showTimekeeperColumn
				? DEFAULT_COLUMN_ORDER
				: DEFAULT_COLUMN_ORDER.filter((id) => id !== "timekeeper"),
		[showTimekeeperColumn],
	);
	const isVisible = (key: string) => columnVisibility[key] !== false;
	const isDefaultVisible = (key: string) =>
		DEFAULT_COLUMN_VISIBILITY[key] !== false;
	const orderedColumnIds = useMemo(() => {
		const allowed = new Set(defaultColumnOrder);
		const seen = new Set<string>();
		const out: string[] = [];
		for (const id of columnOrder) {
			if (!allowed.has(id) || seen.has(id)) continue;
			seen.add(id);
			out.push(id);
		}
		for (const id of defaultColumnOrder) {
			if (!seen.has(id)) out.push(id);
		}
		return out;
	}, [columnOrder, defaultColumnOrder]);
	const toggle = (key: string) =>
		onColumnVisibilityChange({ ...columnVisibility, [key]: !isVisible(key) });
	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;
		const oldIndex = orderedColumnIds.indexOf(String(active.id));
		const newIndex = orderedColumnIds.indexOf(String(over.id));
		if (oldIndex < 0 || newIndex < 0) return;
		onColumnOrderChange(arrayMove(orderedColumnIds, oldIndex, newIndex));
	};
	const hasCustomColumns =
		Object.keys(columnLabels).some(
			(key) => isVisible(key) !== isDefaultVisible(key),
		) || orderedColumnIds.join(",") !== defaultColumnOrder.join(",");

	return (
		<Popover>
			<Tooltip>
				<TooltipTrigger asChild>
					<PopoverTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							aria-label="Columns"
							className="relative size-7 bg-muted text-muted-foreground hover:bg-muted hover:text-foreground"
						>
							<Settings2 className="size-4" />
							{hasCustomColumns && (
								<span className="absolute top-1 right-1 size-1.5 rounded-full bg-ring" />
							)}
						</Button>
					</PopoverTrigger>
				</TooltipTrigger>
				<TooltipContent side="bottom">Columns</TooltipContent>
			</Tooltip>
			<PopoverContent align="end" className="w-56 p-2">
				<div className="px-2 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
					Columns
				</div>
				<DndContext
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={orderedColumnIds}
						strategy={verticalListSortingStrategy}
					>
						<div className="flex flex-col">
							{orderedColumnIds.map((key) => (
								<ColumnMenuRow
									key={key}
									id={key}
									label={columnLabels[key] ?? key}
									checked={isVisible(key)}
									onToggle={() => toggle(key)}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
				{hasCustomColumns && (
					<div className="mt-1 border-t border-border pt-1">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => {
								onColumnVisibilityChange(DEFAULT_COLUMN_VISIBILITY);
								onColumnOrderChange(defaultColumnOrder);
							}}
							className="h-7 w-full text-xs text-muted-foreground hover:text-foreground"
						>
							Reset to default
						</Button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}

function ColumnMenuRow({
	id,
	label,
	checked,
	onToggle,
}: {
	id: string;
	label: string;
	checked: boolean;
	onToggle: () => void;
}) {
	const {
		attributes,
		listeners,
		setActivatorNodeRef,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
			}}
			className={cn("flex items-center rounded-sm", isDragging && "z-10")}
		>
			<Button
				variant="ghost"
				size="sm"
				onClick={onToggle}
				className="h-8 min-w-0 flex-1 justify-start gap-2 rounded-sm px-2 text-xs font-normal hover:bg-accent hover:text-accent-foreground"
			>
				<Checkbox
					checked={checked}
					tabIndex={-1}
					aria-hidden
					className="pointer-events-none shrink-0"
				/>
				<span className="truncate">{label}</span>
			</Button>
			<button
				type="button"
				ref={setActivatorNodeRef}
				aria-label={`Reorder ${label} column`}
				className="flex h-8 w-7 cursor-grab items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground active:cursor-grabbing"
				{...attributes}
				{...listeners}
			>
				<GripVertical className="size-3.5" />
			</button>
		</div>
	);
}

interface ToolbarProps {
	date: Date;
	dateRange: Partial<DateRange>;
	onDateChange: (d: Date) => void;
	onDateRangeChange: (r: DateRange) => void;
	filterSlot: React.ReactNode;
	matterLockSlot: ReactNode;
	searchSlot: ReactNode;
	timerSlot: ReactNode;
	selectedCount: number;
	/**
	 * Exported rows are terminal, and deleted rows must be restored before
	 * normal mutations. Duplicate stays clickable because v1 `/records/duplicate`
	 * produces a fresh tracked copy from any state.
	 */
	selectionHasExported: boolean;
	selectionHasDeleted: boolean;
	selectedDeletedCount: number;
	onClearSelection: () => void;
	onAdd: () => void;
	onExport: () => void;
	onRestoreDeleted: () => void;
	onDelete: () => void;
	onDuplicate: () => void;
	onMarkReviewed: () => void;
	onMarkTracked: () => void;
	onMerge: () => void;
	onGroup: () => void;
	onCollapse: () => void;
}

const TOOLBAR_TWO_ROW_BREAKPOINT_PX = 960;
const TOOLBAR_SELECTED_TWO_ROW_BREAKPOINT_PX = 1120;

function ToolbarControlCluster({
	date,
	dateRange,
	onDateChange,
	onDateRangeChange,
	filterSlot,
	searchSlot,
}: {
	date: Date;
	dateRange: Partial<DateRange>;
	onDateChange: (d: Date) => void;
	onDateRangeChange: (r: DateRange) => void;
	filterSlot: ReactNode;
	searchSlot: ReactNode;
}) {
	const selectedDateRange = isCompleteDateRange(dateRange)
		? dateRange
		: { from: date, to: date };

	return (
		<>
			{filterSlot}
			<DatePicker
				className="w-fit"
				dateRange={selectedDateRange}
				onDateChange={onDateChange}
				onDateRangeChange={onDateRangeChange}
				selectionMode="range"
				bottomSlot={DatePickerDefaultFooter}
			/>
			{searchSlot}
		</>
	);
}

function ToolbarControlsRow({
	left,
	right,
}: {
	left: ReactNode;
	right: ReactNode;
}) {
	return (
		<div className="mb-2 flex min-h-10 flex-wrap items-center gap-2 animate-in fade-in-0 slide-in-from-top-1 duration-150">
			<div className="flex min-w-0 items-center gap-2">{left}</div>
			<div className="ml-auto flex shrink-0 items-center gap-2">{right}</div>
		</div>
	);
}

function Toolbar({
	date,
	dateRange,
	onDateChange,
	onDateRangeChange,
	filterSlot,
	matterLockSlot,
	searchSlot,
	timerSlot,
	selectedCount,
	selectionHasExported,
	selectionHasDeleted,
	selectedDeletedCount,
	onClearSelection,
	onAdd,
	onExport,
	onRestoreDeleted,
	onDelete,
	onDuplicate,
	onMarkReviewed,
	onMarkTracked,
	onMerge,
	onGroup,
	onCollapse,
}: ToolbarProps) {
	const hasSelection = selectedCount >= 1;
	const hasMultiple = selectedCount >= 2;
	const lockedByStatus = selectionHasExported || selectionHasDeleted;
	const statusLockReason = selectionHasDeleted ? "deleted" : "exported";
	const primaryIsRestore = selectionHasDeleted;
	const primaryAction = primaryIsRestore
		? {
				key: "restore",
				label: "Restore",
				Icon: RotateCcw,
				onClick: onRestoreDeleted,
				disabled: selectedDeletedCount === 0,
				className:
					"border border-primary/30 bg-primary/10 text-primary shadow-primary/10 hover:bg-primary/15",
			}
		: hasSelection
			? {
					key: "export",
					label: "Export",
					Icon: Send,
					onClick: onExport,
					disabled: selectionHasExported,
					className:
						"bg-success text-success-foreground shadow-success/25 hover:bg-success/90",
				}
			: {
					key: "export",
					label: "Export",
					Icon: Send,
					onClick: onExport,
					disabled: true,
					className:
						"bg-success text-success-foreground shadow-success/25 hover:bg-success/90",
				};
	const toolbarRef = useRef<HTMLDivElement>(null);
	const frameRef = useRef(0);
	const [centerIsRaised, setCenterIsRaised] = useState(false);
	const twoRowBreakpoint = hasSelection
		? TOOLBAR_SELECTED_TWO_ROW_BREAKPOINT_PX
		: TOOLBAR_TWO_ROW_BREAKPOINT_PX;
	const measureToolbar = useCallback(() => {
		const toolbar = toolbarRef.current;
		if (!toolbar) return;

		cancelAnimationFrame(frameRef.current);
		frameRef.current = requestAnimationFrame(() => {
			setCenterIsRaised(toolbar.clientWidth < twoRowBreakpoint);
		});
	}, [twoRowBreakpoint]);

	useEffect(() => {
		const toolbar = toolbarRef.current;
		if (!toolbar) return;

		measureToolbar();

		if (typeof ResizeObserver === "undefined") {
			window.addEventListener("resize", measureToolbar);
			return () => {
				window.removeEventListener("resize", measureToolbar);
				cancelAnimationFrame(frameRef.current);
			};
		}

		const observer = new ResizeObserver(measureToolbar);
		observer.observe(toolbar);
		window.addEventListener("resize", measureToolbar);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measureToolbar);
			cancelAnimationFrame(frameRef.current);
		};
	}, [measureToolbar]);

	return (
		<TooltipProvider delayDuration={300}>
			<div className="bg-background px-4 pt-4 pb-2">
				{centerIsRaised && (
					<ToolbarControlsRow
						left={
							<ToolbarControlCluster
								date={date}
								dateRange={dateRange}
								onDateChange={onDateChange}
								onDateRangeChange={onDateRangeChange}
								filterSlot={filterSlot}
								searchSlot={searchSlot}
							/>
						}
						right={null}
					/>
				)}

				<div
					ref={toolbarRef}
					className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2"
				>
					{/* Left: bulk-action buttons. Always visible; disabled until the
				    selection is large enough (and not locked by an exported row). */}
					<div className="col-start-1 flex min-w-max items-center gap-2 justify-self-start">
						<ButtonGroup>
							<IconButton
								icon={Merge}
								label={
									lockedByStatus
										? `Merge (selection includes ${statusLockReason})`
										: "Merge"
								}
								onClick={onMerge}
								disabled={!hasMultiple || lockedByStatus}
							/>
							<IconButton
								icon={ListChevronsDownUp}
								label={
									lockedByStatus
										? `Collapse (selection includes ${statusLockReason})`
										: "Collapse"
								}
								onClick={onCollapse}
								disabled={!hasMultiple || lockedByStatus}
							/>
							<IconButton
								icon={Group}
								label={
									lockedByStatus
										? `Group (selection includes ${statusLockReason})`
										: "Group"
								}
								onClick={onGroup}
								disabled={!hasMultiple || lockedByStatus}
							/>
							<IconButton
								icon={Copy}
								label="Duplicate"
								onClick={onDuplicate}
								disabled={!hasSelection}
							/>
						</ButtonGroup>

						<ButtonGroup>
							<IconButton
								iconNode={<StatusIcon status="tracked" />}
								label={
									lockedByStatus
										? `Mark tracked (selection includes ${statusLockReason})`
										: "Mark tracked"
								}
								onClick={onMarkTracked}
								disabled={!hasSelection || lockedByStatus}
							/>
							<IconButton
								iconNode={<StatusIcon status="reviewed" />}
								label={
									lockedByStatus
										? `Mark reviewed (selection includes ${statusLockReason})`
										: "Mark reviewed"
								}
								onClick={onMarkReviewed}
								disabled={!hasSelection || lockedByStatus}
							/>
							<IconButton
								iconNode={<Trash2 className="text-destructive" />}
								label={
									lockedByStatus
										? `Delete (selection includes ${statusLockReason})`
										: "Delete"
								}
								onClick={onDelete}
								disabled={!hasSelection || lockedByStatus}
							/>
						</ButtonGroup>

						{hasSelection && (
							<div className="flex items-center gap-0.5 pl-1 animate-in fade-in-0 slide-in-from-left-2 duration-150">
								<span className="text-sm font-medium tabular-nums">
									{selectedCount} selected
								</span>
								<Button
									variant="ghost"
									size="icon"
									className="size-7 text-muted-foreground"
									onClick={onClearSelection}
									aria-label="Clear selection"
								>
									<X />
								</Button>
							</div>
						)}
					</div>

					{/* Center: filters/search, calendar, timer context. */}
					{!centerIsRaised && (
						<div className="col-start-2 flex shrink-0 items-center gap-2 justify-self-center">
							<ToolbarControlCluster
								date={date}
								dateRange={dateRange}
								onDateChange={onDateChange}
								onDateRangeChange={onDateRangeChange}
								filterSlot={filterSlot}
								searchSlot={searchSlot}
							/>
						</div>
					)}

					{/* Right: matter/timer context beside the primary action. */}
					<div className="col-start-3 flex min-w-max items-center justify-end gap-2 justify-self-end">
						{timerSlot}
						{matterLockSlot}
						<Button variant="outline" aria-label="Add" onClick={onAdd}>
							<Plus />
							Add
						</Button>
						<Button
							variant="default"
							aria-label={primaryAction.label}
							onClick={primaryAction.onClick}
							disabled={primaryAction.disabled}
							className={cn("min-w-28", primaryAction.className)}
						>
							<span className="relative z-10 inline-flex items-center gap-2">
								<span
									key={`${primaryAction.key}-icon`}
									className="inline-flex animate-in fade-in-0 zoom-in-75 spin-in-12 duration-200"
								>
									<primaryAction.Icon />
								</span>
								<span
									key={`${primaryAction.key}-label`}
									className="inline-block animate-in fade-in-0 slide-in-from-right-1 duration-200"
								>
									{primaryAction.label}
								</span>
							</span>
						</Button>
					</div>
				</div>
			</div>
		</TooltipProvider>
	);
}

export default function EntriesView() {
	// View state lives in the URL (nuqs) so a filtered/grouped/sorted table is
	// shareable and survives reload. Each param is omitted while at its default
	// and written with `history: "replace"` so tweaks don't pile up in the back
	// stack. Ephemeral state (rowSelection, columnSizing) stays in memory.
	const [sorting, setSorting] = useQueryState(
		"sort",
		sortingParser
			.withDefault(DEFAULT_SORTING)
			.withOptions({ history: "replace" }),
	);
	const [columnVisibility, setColumnVisibility] = useQueryState(
		"cols",
		visibilityParser
			.withDefault(DEFAULT_COLUMN_VISIBILITY)
			.withOptions({ history: "replace" }),
	);
	const [columnOrder, setColumnOrder] = useQueryState(
		"colOrder",
		columnOrderParser
			.withDefault(DEFAULT_COLUMN_ORDER)
			.withOptions({ history: "replace" }),
	);
	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
	const [expanded, setExpanded] = useState<ExpandedState>(true);

	// The search box stays controlled by local state for snappy typing; only
	// the debounced value is mirrored into the `q` param (and used for Fuse).
	const [qParam, setQParam] = useQueryState(
		"q",
		parseAsString.withDefault("").withOptions({ history: "replace" }),
	);
	const [searchQuery, setSearchQuery] = useState(qParam);
	const debouncedQuery = useDebounce(searchQuery, 150);
	useEffect(() => {
		setQParam(debouncedQuery === "" ? null : debouncedQuery);
	}, [debouncedQuery, setQParam]);

	const { date, dateRange, setDate, setDateRange } = useDateParams();

	const { data: entries, isLoading } = useAllEntries();
	const data = entries ?? [];
	const timekeepers = useConfiguredTimekeepers();
	const showTimekeeperColumn = timekeepers !== null;
	const fieldOptions = useFilterOptions(data, timekeepers);
	const {
		filters,
		defaultFilters,
		toggleFieldValue: toggleFilterFieldValue,
		selectAll: selectAllFilterValues,
		selectNone: selectNoFilterValues,
		reset: resetFilters,
	} = useFilters(fieldOptions);
	const effectiveFilters = filters;

	const scopedData = data;
	const searchHits = useFuseSearch(scopedData, debouncedQuery);

	const { matters } = useMattersCache();
	const { settings, updateSettings } = useSettingsCache();

	const dateFilter = useMemo(
		() => computeDateFilter(date, dateRange),
		[date, dateRange],
	);
	const selectionScopeKey = useMemo(
		() => getSelectionScopeKey(dateFilter, effectiveFilters, debouncedQuery),
		[dateFilter, effectiveFilters, debouncedQuery],
	);
	const { rowSelection, handleRowSelectionChange, clearSelection } =
		useScopedRowSelectionState(selectionScopeKey, {
			restoreOnScopeReturn: false,
		});

	// TanStack keys columnFilters by id. Missing non-status fields are implicit all;
	// explicit empty arrays intentionally match nothing.
	const columnFilters = useMemo<ColumnFiltersState>(() => {
		const out: ColumnFiltersState = [{ id: "date", value: dateFilter }];
		out.push({ id: "status", value: { values: filters.status } });
		if (filters.timekeeper) {
			out.push({ id: "timekeeper", value: { values: [filters.timekeeper] } });
		}
		for (const field of [
			"application",
			"matter",
			"client",
			"billableCategory",
		] as const) {
			const values = filters[field];
			if (values !== undefined) out.push({ id: field, value: { values } });
		}
		return out;
	}, [dateFilter, filters]);

	// Derive selected entries directly from rowSelection + data instead of
	// `table.getSelectedRowModel()` — the table's computed model is not
	// reference-stable, which would re-trigger the action hook's memos on every
	// table render. Scope the derivation to the current date/filter/search view so
	// hidden rows cannot remain selected and be affected by toolbar actions.
	// Exported rows ARE selectable (so they participate in the per-row gestures,
	// e.g. duplicate-many), but the toolbar handlers in `useEntryActions` decide
	// per-action whether to skip them.
	const selectedEntries = useMemo(() => {
		const predicate = makeEntryPredicate(effectiveFilters, dateFilter);
		return scopedData.filter(
			(e) =>
				rowSelection[e.id] &&
				predicate(e) &&
				(searchHits === null || searchHits.has(e.id)),
		);
	}, [scopedData, dateFilter, effectiveFilters, rowSelection, searchHits]);
	const selectedCount = selectedEntries.length;
	const tableColumns = useMemo(
		() => getEntriesColumns(showTimekeeperColumn ? timekeepers : null),
		[showTimekeeperColumn, timekeepers],
	);
	useEffect(() => {
		if (!sorting.some((s) => s.id === "timekeeper")) return;
		const next = sorting.filter((s) => s.id !== "timekeeper");
		setSorting(next.length > 0 ? next : DEFAULT_SORTING);
	}, [sorting, setSorting]);
	const visibleColumnOrder = useMemo(
		() =>
			showTimekeeperColumn
				? columnOrder
				: columnOrder.filter((id) => id !== "timekeeper"),
		[columnOrder, showTimekeeperColumn],
	);
	const tableColumnOrder = useMemo(
		() => [
			"_select",
			...visibleColumnOrder,
			"client",
			"task",
			"activity",
			"matterName",
			"billable",
			"unitsSeconds",
		],
		[visibleColumnOrder],
	);
	const actions = useEntryActions({
		selectedEntries,
		clearSelection,
		timekeeperId: filters.timekeeper ?? null,
	});

	// Grouping follows the active sort column; defaults to date when unsorted.
	const effectiveSorting = useMemo(
		() => sorting.filter((s) => s.id !== "timekeeper"),
		[sorting],
	);
	const sortBy = effectiveSorting[0]?.id ?? "date";
	const grouping = useMemo<GroupingState>(() => [sortBy], [sortBy]);
	const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
		(updater) => {
			// Group row ids are derived from the active grouping/sort column. If the
			// user previously collapsed one group, `expanded` becomes a sparse object;
			// after a sort/group change that object does not describe the new grouped
			// row model, so the table appears fully collapsed. Match the mobile list's
			// default behavior by reopening all groups whenever sorting changes.
			setExpanded(true);
			setSorting(updater);
		},
		[setSorting],
	);

	const table = useReactTable({
		data: scopedData,
		columns: tableColumns,
		state: {
			sorting: effectiveSorting,
			grouping,
			expanded,
			columnVisibility,
			columnOrder: tableColumnOrder,
			rowSelection,
			columnFilters,
			columnSizing,
			globalFilter: searchHits,
		},
		onSortingChange: handleSortingChange,
		onExpandedChange: setExpanded,
		onColumnVisibilityChange: setColumnVisibility,
		onColumnOrderChange: setColumnOrder,
		onRowSelectionChange: handleRowSelectionChange,
		onColumnSizingChange: setColumnSizing,
		enableRowSelection: (row) => !row.getIsGrouped(),
		enableGrouping: true,
		autoResetExpanded: false,
		enableColumnResizing: true,
		groupedColumnMode: false,
		// `onEnd` commits the new size once at mouseup instead of on every
		// mousemove. During drag the body grid stays put — only the resize
		// handle slides via `translateX(deltaOffset)` (see DataTableResizeHandle).
		// Avoids per-frame grid relayout of every row.
		columnResizeMode: "onEnd",
		defaultColumn: { minSize: 40, maxSize: 800 },
		getRowId: (row) => row.id,
		getCoreRowModel: getCoreRowModel(),
		getGroupedRowModel: getGroupedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		globalFilterFn: (row: Row<Entry>, _id, value) =>
			value == null || (value as Set<string>).has(row.original.id),
		meta: {
			rowActions: actions.rowActions,
		} satisfies EntriesViewMeta,
	});

	const statsEntries = useMemo(() => {
		const predicate = makeEntryPredicate(
			{
				status: ["tracked", "reviewed", "exported", "deleted"],
				timekeeper: filters.timekeeper,
			},
			dateFilter,
		);
		return scopedData.filter(predicate);
	}, [scopedData, dateFilter, filters.timekeeper]);

	const filterMenuSharedProps = {
		fieldOptions,
		filters: effectiveFilters,
		defaultFilters,
		onToggleFieldValue: toggleFilterFieldValue,
		onSelectAll: selectAllFilterValues,
		onSelectNone: selectNoFilterValues,
		onReset: resetFilters,
	};

	const hasActiveFilters = !isDefaultFilters(effectiveFilters, defaultFilters);

	return (
		<div className="w-full h-full flex flex-col">
			<Toolbar
				date={date}
				dateRange={dateRange}
				onDateChange={setDate}
				onDateRangeChange={setDateRange}
				filterSlot={
					<FilterMenu
						{...filterMenuSharedProps}
						trigger={
							<Button
								variant="outline"
								size="icon"
								aria-label="Filter"
								className={cn(
									"relative",
									hasActiveFilters &&
										"border-ring text-ring hover:border-ring hover:text-ring",
								)}
							>
								<Filter />
								{hasActiveFilters && (
									<span className="absolute right-1 top-1 size-1.5 rounded-full bg-ring" />
								)}
							</Button>
						}
					/>
				}
				matterLockSlot={
					<MatterLockButton
						matters={matters}
						value={settings.hardcoded_matter}
						onChange={(id) => updateSettings({ hardcoded_matter: id })}
					/>
				}
				searchSlot={
					<SearchBar
						value={searchQuery}
						onChange={setSearchQuery}
						onClear={() => setSearchQuery("")}
					/>
				}
				timerSlot={<EntriesTimer onAddEntry={actions.onAddWithDuration} />}
				selectedCount={selectedCount}
				selectionHasExported={actions.selectionHasExported}
				selectionHasDeleted={actions.selectionHasDeleted}
				selectedDeletedCount={actions.selectedDeletedCount}
				onClearSelection={clearSelection}
				onAdd={actions.onAdd}
				onExport={actions.onExport}
				onRestoreDeleted={actions.onRestoreDeleted}
				onDelete={actions.onDelete}
				onDuplicate={actions.onDuplicate}
				onMarkReviewed={actions.onMarkReviewed}
				onMarkTracked={actions.onMarkTracked}
				onMerge={actions.onMerge}
				onGroup={actions.onGroup}
				onCollapse={actions.onCollapse}
			/>
			<div className="flex-1 min-h-0 px-4 pb-4 pt-1">
				<div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-background">
					<TooltipProvider delayDuration={300}>
						<div className="relative flex-1 min-h-0">
							<EntriesTable
								table={table}
								isLoading={isLoading}
								rowSelection={rowSelection}
								onRowClick={actions.onRowClick}
								timekeepers={timekeepers}
								columnsMenu={
									<ColumnsMenu
										columnVisibility={columnVisibility}
										onColumnVisibilityChange={setColumnVisibility}
										columnOrder={columnOrder}
										onColumnOrderChange={setColumnOrder}
										showTimekeeperColumn={showTimekeeperColumn}
									/>
								}
							/>
						</div>
					</TooltipProvider>

					<StatsBar entries={statsEntries} />
				</div>
			</div>

			{actions.dialogs}
		</div>
	);
}
